import { AlertCircle, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useTranslation } from '@/hooks/useTranslation'
import { BpmnSaveStatus } from './BpmnSaveStatus'
import type { BpmnExternalToolbarApi } from './bpmnExternalToolbar'

type BpmnModelSaveControlsProps = Pick<
	BpmnExternalToolbarApi,
	'loading' | 'saveStatus' | 'isSaveInFlight' | 'isSaveStalled' | 'onRetrySave' | 'hasAutosave'
> & {
	/** When false, omit the text status (the history chip already shows save animation). */
	showStatus?: boolean
}

export function BpmnModelSaveControls({
	loading,
	saveStatus,
	isSaveInFlight,
	isSaveStalled,
	onRetrySave,
	hasAutosave,
	showStatus = true,
}: BpmnModelSaveControlsProps) {
	const { t } = useTranslation()

	if (!hasAutosave) return null

	return (
		<div className="flex flex-wrap items-center gap-2">
			{showStatus ? <BpmnSaveStatus status={saveStatus} /> : null}
			<Tooltip>
				<TooltipTrigger asChild>
					<Button
						type="button"
						variant="ghost"
						size="icon-sm"
						className="shrink-0 text-muted-foreground hover:text-foreground"
						aria-label={t('components.bpmnEditor.autosaveHintAria')}
					>
						<Info className="h-4 w-4" />
					</Button>
				</TooltipTrigger>
				<TooltipContent side="top" className="max-w-xs">
					<p>{t('components.bpmnEditor.autosaveHint')}</p>
				</TooltipContent>
			</Tooltip>
			{showStatus && saveStatus === 'error' ? (
				<Button
					onClick={() => void onRetrySave()}
					disabled={loading}
					size="sm"
					variant="outline"
					className="border-yellow-200 bg-yellow-50 text-yellow-800 hover:bg-yellow-100 hover:text-yellow-900"
				>
					<AlertCircle className="mr-2 h-4 w-4" />
					{t('pages.analysisDetail.validateModel.retrySave')}
				</Button>
			) : null}
			{isSaveInFlight && isSaveStalled ? (
				<Button
					onClick={() => void onRetrySave()}
					disabled={loading}
					size="sm"
					variant="outline"
					className="border-yellow-200 bg-yellow-50 text-yellow-800 hover:bg-yellow-100 hover:text-yellow-900"
				>
					<AlertCircle className="mr-2 h-4 w-4" />
					{t('components.bpmnEditor.forceSave')}
				</Button>
			) : null}
		</div>
	)
}
